import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  Package, Clock, CheckCircle, Truck, AlertCircle, ArrowLeft, 
  MapPin, Phone, Mail, CreditCard, Calendar, XCircle, 
  ChevronRight, HelpCircle, MessageCircle, Copy, Check
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getPaymentDisplay, getPaymentMethodLabel } from '@/lib/paymentDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusSteps = [
  { key: 'pending', label: 'Order Placed', icon: Clock, description: 'Waiting for confirmation' },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle, description: 'Order confirmed by seller' },
  { key: 'processing', label: 'Processing', icon: Package, description: 'Preparing your order' },
  { key: 'shipped', label: 'Shipped', icon: Truck, description: 'On the way to you' },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle, description: 'Order delivered' }
];

const OrderTrackingPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchOrder = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/customer/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data);
    } catch (err) {
      setError('Order not found or you do not have permission to view it.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchOrder();
  }, [orderId, isAuthenticated, navigate]);

  const handleCancelOrder = async () => {
    setCancelling(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/customer/orders/${orderId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Order cancelled successfully');
      setShowCancelDialog(false);
      fetchOrder(); // Refresh order data
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to cancel order:', err);
      toast.error(err.response?.data?.detail || 'Failed to cancel order');
    } finally {
      setCancelling(false);
    }
  };

  const copyOrderId = () => {
    navigator.clipboard.writeText(orderId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getExpectedDelivery = () => {
    const date = new Date(order?.created_at);
    date.setDate(date.getDate() + 7);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getCurrentStepIndex = () => {
    if (order?.order_status === 'cancelled') return -1;
    return statusSteps.findIndex(step => step.key === order?.order_status);
  };

  const canCancel = () => {
    // Customer can ONLY cancel if order is still pending (before admin confirms)
    return order?.order_status === 'pending';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center pb-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#0047AB]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 pb-20">
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="bg-white rounded-xl p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Order Not Found</h2>
            <p className="text-slate-500 mb-6">{error}</p>
            <Button onClick={() => navigate('/my-orders')} className="bg-[#0047AB]">
              View All Orders
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const currentStep = getCurrentStepIndex();
  const isCancelled = order?.order_status === 'cancelled';

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      <div className="max-w-3xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/my-orders')}
            className="text-slate-600 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            My Orders
          </Button>
          
          <button
            onClick={copyOrderId}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
          >
            <span className="font-mono">#{orderId}</span>
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>

        {/* Order Status Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm overflow-hidden mb-4"
        >
          {/* Status Header */}
          <div className={`px-4 py-5 ${isCancelled ? 'bg-red-50' : 'bg-gradient-to-r from-[#0047AB] to-[#0066FF]'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {isCancelled ? (
                  <XCircle className="w-10 h-10 text-red-500" />
                ) : currentStep === statusSteps.length - 1 ? (
                  <CheckCircle className="w-10 h-10 text-white" />
                ) : (
                  <Truck className="w-10 h-10 text-white" />
                )}
                <div>
                  <h1 className={`text-lg font-bold ${isCancelled ? 'text-red-700' : 'text-white'}`}>
                    {isCancelled ? 'Order Cancelled' : statusSteps[currentStep]?.label || 'Processing'}
                  </h1>
                  <p className={`text-sm ${isCancelled ? 'text-red-600' : 'text-white/80'}`}>
                    {isCancelled 
                      ? `Cancelled on ${formatDate(order.cancelled_at || order.created_at)}`
                      : statusSteps[currentStep]?.description
                    }
                  </p>
                </div>
              </div>
              
              {!isCancelled && order?.order_status !== 'delivered' && (
                <div className="text-right">
                  <p className="text-white/70 text-xs">Expected by</p>
                  <p className="text-white font-semibold">{getExpectedDelivery()}</p>
                </div>
              )}
            </div>
          </div>

          {/* Progress Steps */}
          {!isCancelled && (
            <div className="px-4 py-6">
              <div className="relative">
                {/* Progress Line */}
                <div className="absolute top-5 left-5 right-5 h-0.5 bg-slate-200">
                  <div 
                    className="h-full bg-[#0047AB] transition-all duration-500"
                    style={{ width: `${(currentStep / (statusSteps.length - 1)) * 100}%` }}
                  />
                </div>
                
                {/* Steps */}
                <div className="relative flex justify-between">
                  {statusSteps.map((step, index) => {
                    const isCompleted = index <= currentStep;
                    const isCurrent = index === currentStep;
                    const StepIcon = step.icon;
                    
                    return (
                      <div key={step.key} className="flex flex-col items-center">
                        <div 
                          className={`w-10 h-10 rounded-full flex items-center justify-center z-10 transition-all ${
                            isCompleted 
                              ? 'bg-[#0047AB] text-white' 
                              : 'bg-slate-200 text-slate-400'
                          } ${isCurrent ? 'ring-4 ring-blue-100' : ''}`}
                        >
                          <StepIcon className="w-5 h-5" />
                        </div>
                        <span className={`text-xs mt-2 text-center max-w-[60px] ${
                          isCompleted ? 'text-[#0047AB] font-medium' : 'text-slate-400'
                        }`}>
                          {step.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </motion.div>

        {/* Order Items */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm p-4 mb-4"
        >
          <h2 className="font-semibold text-slate-900 mb-4">Order Items</h2>
          <div className="space-y-3">
            {order?.items?.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  {item.image_url ? (
                    <img src={item.image_url} alt={item.product_name} className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <Package className="w-6 h-6 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate">{item.product_name}</p>
                  <p className="text-sm text-slate-500">Qty: {item.quantity} × {formatPrice(item.price)}</p>
                </div>
                <p className="font-semibold text-slate-900">{formatPrice(item.price * item.quantity)}</p>
              </div>
            ))}
          </div>
          
          {/* Total */}
          <div className="border-t border-slate-100 mt-4 pt-4 flex justify-between items-center">
            <span className="font-medium text-slate-700">Total Amount</span>
            <span className="text-xl font-bold text-[#0047AB]">{formatPrice(order?.total_amount)}</span>
          </div>
        </motion.div>

        {/* Delivery & Payment Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* Delivery Address */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-sm p-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-5 h-5 text-[#0047AB]" />
              <h3 className="font-semibold text-slate-900">Delivery Address</h3>
            </div>
            <p className="text-sm text-slate-700 font-medium">{order?.customer_name}</p>
            <p className="text-sm text-slate-600 mt-1">{order?.delivery_address}</p>
            <div className="flex items-center gap-4 mt-3 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Phone className="w-4 h-4" />
                {order?.customer_phone}
              </span>
            </div>
          </motion.div>

          {/* Payment Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-sm p-4"
          >
            <div className="flex items-center gap-2 mb-3">
              <CreditCard className="w-5 h-5 text-[#0047AB]" />
              <h3 className="font-semibold text-slate-900">Payment Details</h3>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Payment Status</span>
                <span className={`font-medium ${getPaymentDisplay(order).color}`} data-testid="payment-status-text">
                  {getPaymentDisplay(order).label}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Payment Method</span>
                <span className="text-slate-700">{getPaymentMethodLabel(order?.payment_method)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Order Date</span>
                <span className="text-slate-700">{formatDate(order?.created_at)}</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-sm p-4"
        >
          <h3 className="font-semibold text-slate-900 mb-4">Need Help?</h3>
          <div className="grid grid-cols-2 gap-3">
            <Link
              to="/contact"
              className="flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-3 rounded-lg transition-colors"
            >
              <HelpCircle className="w-5 h-5" />
              Contact Support
            </Link>
            <a
              href={`https://wa.me/971503847865?text=Hi, I need help with order ${orderId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-medium py-3 rounded-lg transition-colors"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp
            </a>
          </div>
          
          {/* Cancel Button */}
          {canCancel() && (
            <Button
              variant="outline"
              onClick={() => setShowCancelDialog(true)}
              className="w-full mt-3 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="cancel-order-btn"
            >
              <XCircle className="w-4 h-4 mr-2" />
              Cancel Order
            </Button>
          )}
        </motion.div>
      </div>

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Order?</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this order? This action cannot be undone.
              {order?.payment_status === 'paid' && (
                <span className="block mt-2 text-amber-600">
                  Your payment will be refunded within 5-7 business days.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Keep Order
            </Button>
            <Button 
              onClick={handleCancelOrder}
              disabled={cancelling}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cancel-btn"
            >
              {cancelling ? 'Cancelling...' : 'Yes, Cancel Order'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderTrackingPage;
